import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

/* Rechnungsversand per E-Mail (12.10.2026): Die Rechnung ist die Vorabankündigung der Lastschrift — aber nur, wenn
   sie den Zahler erreicht. Schalter (Standard aus), Stand je Rechnung, Vorschau und Testmail an die eigene Adresse. */

const STAND = {
  gesendet: ['gesendet', 'text-emerald-700 dark:text-emerald-400'],
  keine_email: ['keine E-Mail-Adresse', 'text-amber-700 dark:text-amber-400'],
  geblockt: ['Adresse gesperrt', 'text-amber-700 dark:text-amber-400'],
  deaktiviert: ['Mailversand ist aus', 'text-amber-700 dark:text-amber-400'],
  abruf_fehler: ['PDF nicht abrufbar', 'text-amber-700 dark:text-amber-400'],
  fehler: ['unklar, bitte prüfen', 'text-red-600'],
  in_arbeit: ['Versand läuft oder unterbrochen', 'text-amber-700 dark:text-amber-400'],
};
const zeit = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const fehlertext = (e, ersatz) => e?.response?.data?.detail || ersatz;

export function VersandKarte({ monat, zaehler }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => api.get('/lexware/versand', { params: { monat } }).then(r => setD(r.data)).catch(() => {}), [monat]);
  useEffect(() => { laden(); }, [laden, zaehler]);
  if (!d) return null;
  const schalten = async () => {
    setBusy(true);
    try { await api.put('/lexware/versand', { aktiv: !d.aktiv }); toast.success(d.aktiv ? 'Rechnungsversand aus' : 'Rechnungsversand an'); laden(); }
    catch (e) { toast.error(fehlertext(e, 'Nicht geändert')); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border border-border p-3 text-sm space-y-2" data-testid="rl-versand">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <b className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" />Rechnungen per E-Mail an den Zahler</b>
          <p className="text-xs text-muted-foreground">
            {d.aktiv
              ? `An seit ${zeit(d.ab)}. Jede Rechnung, die du ab da freigibst, geht innerhalb weniger Minuten mit PDF an den Zahler. Ältere gehen nur von Hand raus.`
              : 'Aus. Rechnungen werden festgeschrieben, aber nicht verschickt. Die Mail nennt Betrag, Mandat und Einzugstermin und gilt damit als Vorabankündigung der Lastschrift.'}
          </p>
        </div>
        <Button size="sm" variant={d.aktiv ? 'outline' : 'default'} disabled={busy} onClick={schalten} data-testid="rl-versand-schalter">
          {d.aktiv ? 'Ausschalten' : 'Einschalten'}
        </Button>
      </div>
      {d.nicht_zugestellt?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />{d.nicht_zugestellt.length} Rechnungen haben den Zahler nicht erreicht
          </summary>
          <ul className="mt-1 space-y-0.5">
            {d.nicht_zugestellt.map(r => <li key={r.id}>{r.zahler_name} · {r.rechnungsnummer}: {(STAND[r.versand?.status] || [r.versand?.status])[0]}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

export function VersandZeile({ rv, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState('');
  const stand = rv.versand?.status;
  const [lab, cls] = STAND[stand] || ['', ''];
  const oeffnen = async () => {
    setOffen(true); setV(null);
    try { setV((await api.get(`/lexware/rechnungen/${rv.id}/versand-vorschau`)).data); }
    catch (e) { toast.error(fehlertext(e, 'Vorschau nicht geladen')); setOffen(false); }
  };
  const senden = async () => {
    setBusy('senden');
    try {
      const r = (await api.post(`/lexware/rechnungen/${rv.id}/senden`, { erneut: !!stand })).data;
      if (r.status === 'gesendet') toast.success(`Gesendet an ${r.an}`); else toast.error(`Nicht gesendet: ${(STAND[r.status] || [r.status])[0]}`);
      setOffen(false); onFertig?.();
    } catch (e) { toast.error(fehlertext(e, 'Nicht gesendet')); }
    setBusy('');
  };
  const test = async () => {
    setBusy('test');
    try {
      const r = (await api.post(`/lexware/rechnungen/${rv.id}/testmail`)).data;
      if (r.status === 'gesendet') toast.success(`Testmail an ${r.an} gesendet`); else toast.error(`Testmail nicht gesendet: ${r.grund || r.status}`);
    } catch (e) { toast.error(fehlertext(e, 'Testmail nicht gesendet')); }
    setBusy('');
  };
  return (
    <>
      <button className="text-xs underline inline-flex items-center gap-1" onClick={oeffnen} data-testid={`rl-mail-${rv.id}`}><Mail className="w-3 h-3" />Mail</button>
      {stand && <div className={`mt-1 text-[11px] ${cls}`}>{lab}{stand === 'gesendet' && rv.versand?.am ? ` ${zeit(rv.versand.am)}` : ''}</div>}
      <Dialog open={offen} onOpenChange={setOffen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Rechnung {rv.rechnungsnummer} per E-Mail</DialogTitle></DialogHeader>
          {!v ? <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Vorschau wird geladen</p> : (
            <div className="space-y-3 text-sm min-w-0">
              <div className="text-xs text-muted-foreground">
                An: <b className="text-foreground">{v.an || 'keine E-Mail-Adresse hinterlegt'}</b> · Anhang: {v.anhang}
                {v.versand?.status && <> · bisher: {(STAND[v.versand.status] || [v.versand.status])[0]}{v.versand.an ? ` an ${v.versand.an}` : ''}{v.versand.am ? `, ${zeit(v.versand.am)}` : ''}</>}
              </div>
              <div className="font-semibold">{v.betreff}</div>
              <pre className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/30 p-3 text-xs font-sans">{v.text}</pre>
              {!v.einzug && <p className="text-xs text-amber-700 dark:text-amber-400">Zu dieser Rechnung gibt es kein gültiges Mandat. Die Mail kündigt keinen Einzug an.</p>}
              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" disabled={!!busy} onClick={test}>{busy === 'test' ? 'Sendet …' : 'Testmail an mich'}</Button>
                <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy || !v.an} onClick={senden}>
                  {busy === 'senden' ? 'Sendet …' : stand === 'gesendet' ? 'Noch einmal an den Zahler senden' : 'An den Zahler senden'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
